import React from "react";
import { useField } from "@2po/formalizr";
import * as style from "./style.module.scss";
import { Input } from "@2po-dpam/components";

import { useContentfulFormalizr } from "@hooks";
import { fillDynamicsForm } from "@utils/dynamics";
import { sortValueLabelArrayByLabel } from "@utils/formalizr";

type FormalizrCheckboxGroupProps = {
    name: string;
};

const FormalizerCheckboxGroup = ({ name }: FormalizrCheckboxGroupProps) => {
    const { formName } = useContentfulFormalizr();
    const fields = useField({ name });
    const [field, meta, locale] = fields;

    const handleChange = (value: string, checked: boolean) => {
        const currValues = field.value;
        const newValues = currValues.includes(value)
            ? currValues.filter((v: string) => v !== value)
            : [...currValues, value];

        fillDynamicsForm(formName, value, checked);

        field.onChange({
            target: {
                value: newValues,
                name: field.name,
            },
        });
    };

    const items = Object.entries(meta?.widget?.options?.["i18n"] ?? {}).map(
        ([key, value]) => ({
            value: key,
            label: (value as Record<string, string>)[locale] ?? key,
        }),
    );

    const useColumns = items.length > 4;

    const sortedItems = sortValueLabelArrayByLabel(
        items,
        meta?.widget?.options?.["sort"],
    );

    const showErrorMessage = () => meta.error && meta.touched;

    return (
        <div className={style.checkboxGroup} data-testid="form-checkbox-group">
            {field.label[locale] && (
                <label className={style.label}>{field.label[locale]}</label>
            )}
            <ul
                className={`${style.checkboxesContainer} ${
                    useColumns ? style.twoColumns : ""
                }`}
            >
                {sortedItems.map(item => (
                    <li className={style.checkboxItem} key={item.value}>
                        <Input
                            className={style.checkboxInput}
                            id={`option-${item.value}`}
                            onChange={event =>
                                handleChange(item.value, event.target.checked)
                            }
                            type="checkbox"
                            value={item.value}
                        />
                        <label htmlFor={`option-${item.value}`}>
                            {item.label}
                        </label>
                    </li>
                ))}
            </ul>
            {showErrorMessage() && <p className={style.error}>{meta.error}</p>}
        </div>
    );
};

export default FormalizerCheckboxGroup;
